import React, { useState, useEffect, MouseEvent } from "react";
import styled from "styled-components";
import { lighten } from "polished";
import MessageStatusIcon from "./MessageStatusIcon";
import IconButton from "./IconButton";
import { parseSubject, isResponse } from "../SubjectUtils";

const StyledMessageRow = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.colors.gray2};
  cursor: pointer;

  .row {
    padding: 0.6rem;
    display: flex;
    justify-content: space-between;
    font-size: 0.9rem;
  }

  &:hover {
    background-color: ${(props) => lighten(0.1, props.theme.colors.black)};
  }

  .status-icon {
    margin-right: 0.5rem;
  }

  .subject {
    > span {
      color: ${(props) => props.theme.colors.mutedText};
    }
  }

  .right-col {
    svg {
      margin: 0 0.5rem;
    }
    height: 17px;
  }

  .expanded-message {
    margin: 0 0.6rem 0.6rem 0.6rem;
    position: relative;
    overflow: hidden;
  }
`;

interface Props {
  message: any;
  expanded: boolean;
  onMouseEnter: (message: any) => void;
  onCopy: () => void;
  onShowTransaction: () => void;
  onReplay: () => void;
  onFavorite: () => void;
}

const MessageRow: React.FC<Props> = ({
  message,
  expanded,
  onMouseEnter,
  onCopy,
  onShowTransaction,
  onReplay,
  onFavorite,
}) => {
  const [isExpanded, setExpanded] = useState(expanded);
  const { subject, msg } = message;
  const { ms, status } = msg;
  const stringifiedMsg = JSON.stringify(msg, null, 2);
  const { subjectRequestPart, subjectResponsePart } = parseSubject(subject);
  const isAResponse = isResponse(subject);
  const [showAddedToFavFeedbackMessage, setShowAddedToFavFeedbackMessage] = useState(false);

  const toggleRow = () => {
    setExpanded(!isExpanded);
  };

  useEffect(() => {
    setExpanded(expanded);
  }, [expanded]);

  const handleCopy = (event: MouseEvent) => {
    event.stopPropagation();
    onCopy();
  };

  const handleShowTransaction = (event: MouseEvent) => {
    event.stopPropagation();
    onShowTransaction();
  };

  const handleReplay = (event: MouseEvent) => {
    event.stopPropagation();
    onReplay();
  };

  const handleFavorite = (event: MouseEvent) => {
    event.stopPropagation();
    onFavorite();
    setShowAddedToFavFeedbackMessage(true);
    setTimeout(() => {
      setShowAddedToFavFeedbackMessage(false);
    }, 1000);
  };

  return (
    <StyledMessageRow onMouseEnter={() => onMouseEnter(message)}>
      <div className="row" onClick={toggleRow}>
        <div>
          <MessageStatusIcon status={isAResponse ? status : null} className="status-icon" />
          <span className="subject">
            <span>{subjectResponsePart}</span>
            {subjectRequestPart} {ms ? `(${ms} ms)` : ""}
          </span>
        </div>
        <div className="right-col">
          {showAddedToFavFeedbackMessage ? (
            <span style={{ fontSize: "12px" }}>Message was added to library</span>
          ) : (
            <>
              <IconButton icon="favorite" size="s" onClick={handleFavorite} />
              <IconButton icon="replay" size="s" onClick={handleReplay} />
              <IconButton icon="copy" size="s" onClick={handleCopy} />
              <IconButton icon="transaction" size="s" onClick={handleShowTransaction} />
            </>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="expanded-message">
          <pre>
            <code>{stringifiedMsg}</code>
          </pre>
        </div>
      )}
    </StyledMessageRow>
  );
};

export default MessageRow;
